import React, { memo, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import _ from "lodash";
import { Button, Modal, Tabs } from "@toddle-edu/ds-web";
import { DataGrid } from "@toddle-edu/ds-data-grid";
import {
  resolveFlexHeaders,
  reorderArrayItems,
} from "TranscriptsV3/modules/utils";
import CustomGrid from "./components/CustomGrid";
import classes from "./GenericCustomFieldModal.scss";
import { useTranslation } from "react-i18next";
import {
  GENERIC_CUSTOM_FIELD_DATA_GRID_CONTAINER_WIDTH,
  GENERIC_CUSTOM_FIELD_MODAL_HEIGHT,
  GENERIC_CUSTOM_FIELD_MODAL_WIDTH,
  TOOLTIP_DELAY_TIME,
} from "./modules/constants";

const BODY_PADDING_STYLE = { paddingLeft: 0, paddingRight: 0 };
const BODY_STYLE = { ...BODY_PADDING_STYLE, minHeight: 0, overflow: "hidden" };

const GenericCustomFieldModal = ({
  testId,
  title,
  subtitle,
  onClose,
  onSave,
  isSaving,
  tabs,
  selectedTab,
  onTabChange,
  content,
}) => {
  const { t } = useTranslation();
  const hasTabs = _.size(tabs) > 1;
  const contentType = content?.type;

  const bodyContentClassName = classNames(
    classes.content,
    classes.contentWithoutOuterScroll,
    {
      [classes.contentWithTabs]: hasTabs,
    }
  );

  const renderedContent = useMemo(() => {
    if (contentType === "customGrid") {
      const handleRowsReorder = (startIdx, endIdx) => {
        const { items, onItemsUpdate } = content?.config || {};
        if (items && onItemsUpdate) {
          onItemsUpdate(reorderArrayItems(items, startIdx, endIdx));
        }
      };

      const updatedConfig = {
        ...content.config,
        onRowsReorder: handleRowsReorder,
      };

      const customText = content?.config?.customText;

      return (
        <>
          {customText ? (
            <p className={classNames("text-body", classes.subtext)}>
              {customText}
            </p>
          ) : null}
          <div className={classes.customGridContent}>
            <CustomGrid config={updatedConfig} />
          </div>
        </>
      );
    } else if (contentType === "dataGrid") {
      const { customText, data, headers, showSkeletonLoader } = content.config;
      const finalHeaders = resolveFlexHeaders({
        headers,
        containerWidth: GENERIC_CUSTOM_FIELD_DATA_GRID_CONTAINER_WIDTH,
      });

      return (
        <>
          {customText ? (
            <p className={classNames("text-body", classes.subtext)}>
              {customText}
            </p>
          ) : null}
          <div className={classes.dataGridContainer}>
            <div className={classes.dataGrid}>
              <DataGrid
                data={data || []}
                headers={finalHeaders || []}
                rowKey="rowId"
                rowMarkers="none"
                dataGridHeight="100%"
                columnsResizable={false}
                columnSelectionEnabled={false}
                showHoverState={false}
                showSkeletonLoader={showSkeletonLoader || false}
                tooltipDelayTime={TOOLTIP_DELAY_TIME}
              />
            </div>
          </div>
        </>
      );
    }
    return null;
  }, [contentType, content]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      width={GENERIC_CUSTOM_FIELD_MODAL_WIDTH}
      height={GENERIC_CUSTOM_FIELD_MODAL_HEIGHT}
    >
      <Modal.Header
        title={title ?? ""}
        subtitle={subtitle ?? ""}
        onClose={onClose}
        testId={testId}
      />
      <Modal.Body isScrollable={false} style={BODY_STYLE}>
        <div className={classes.body}>
          {hasTabs ? (
            <div className={classes.tabs}>
              <Tabs
                options={tabs}
                value={selectedTab}
                onChange={onTabChange}
                name={testId}
                testId={testId}
              />
            </div>
          ) : null}
          <div className={bodyContentClassName}>{renderedContent}</div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.footer}>
          <div className={classes.footerRight}>
            <Button
              type="outlined"
              variant="neutral"
              onClick={onClose}
              testId={`${testId}-cancel`}
            >
              {t("common:cancel")}
            </Button>
            <Button
              type="fill"
              variant="destructive"
              onClick={onSave}
              disabled={isSaving}
              testId={`${testId}-save`}
            >
              {t("common:save")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

GenericCustomFieldModal.propTypes = {
  testId: PropTypes.string.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
  ),
  selectedTab: PropTypes.string,
  onTabChange: PropTypes.func,
  content: PropTypes.shape({
    type: PropTypes.oneOf(["customGrid", "dataGrid"]).isRequired,
    config: PropTypes.object.isRequired,
  }),
};

GenericCustomFieldModal.defaultProps = {
  title: "",
  subtitle: "",
  isSaving: false,
  tabs: null,
  selectedTab: null,
  onTabChange: null,
  content: null,
};

export default memo(GenericCustomFieldModal);
